import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAppNotificationsStore } from '@/stores/app-notifications-store';
import { useAppMounts } from '@/lib/apps-mounts';
import { getEmbedHandle } from '@/lib/app-embed-registry';

/**
 * Host-side notification bell rendered in the site header. Aggregates items
 * published by every embedded app via `rumio:notifications` (contract v1.10),
 * groups them under the publishing app's name, and forwards user clicks /
 * mark-all-read actions back to the right iframe.
 *
 * Hidden entirely while no app has published anything — we don't want a
 * permanent empty bell in the chrome.
 *
 * Clicking an item posts `rumio:notification-action {click, id}` and
 * navigates the host URL to the app's mount prefix, so the in-iframe
 * navigation triggered by the app lands inside a visible app surface.
 */
export default function NotificationBell() {
  const entries = useAppNotificationsStore((s) => s.byAppId);
  const { data: mounts } = useAppMounts();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  // Aggregate across apps. Preserve publish order via Object.values, then
  // sort sections alphabetically so a new app's notifications don't shuffle
  // the existing layout on the next poll.
  const sections = useMemo(() => {
    return Object.values(entries)
      .filter((e) => e.items.length > 0)
      .sort((a, b) => a.appName.localeCompare(b.appName));
  }, [entries]);
  const totalUnread = sections.reduce((sum, s) => sum + s.unreadCount, 0);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Bell only appears once the iframe has published. This avoids a permanent
  // empty bell during anonymous browsing and during the boot window between
  // iframe mount and first `rumio:notifications`.
  if (sections.length === 0) return null;

  function findMountPrefix(appId: string): string | null {
    const m = mounts?.find((mm) => mm.app.id === appId);
    return m ? `/${m.path_prefix}` : null;
  }

  function onItemClick(appId: string, itemId: string) {
    getEmbedHandle(appId)?.postNotificationAction('click', itemId);
    setOpen(false);
    // Land the user on the app's mount surface so the app's in-iframe
    // navigation (triggered by its own click handler) renders somewhere
    // visible. Without this, the user could be on a marketing page when
    // the app navigates internally and never see the result.
    const prefix = findMountPrefix(appId);
    if (prefix) navigate(prefix);
  }

  function onMarkAllRead(appId: string) {
    getEmbedHandle(appId)?.postNotificationAction('mark-all-read');
  }

  return (
    <div className="relative hidden lg:block" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label="Notifications"
        className="relative flex items-center gap-1 p-2 text-sage-800 hover:text-sage-900 min-w-[44px] min-h-[44px] rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600"
      >
        <Bell className="h-5 w-5" />
        {totalUnread > 0 && (
          <span
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-medium text-white"
            aria-label={`${totalUnread} unread`}
          >
            {totalUnread > 9 ? '9+' : totalUnread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div
            role="menu"
            className="absolute right-0 mt-1 w-80 bg-surface rounded-lg shadow-lg border border-sage-200 z-20 py-1 max-h-[28rem] overflow-y-auto"
          >
            {sections.map((section) => (
              <div key={section.appId}>
                <div className="flex items-center justify-between px-4 py-2 border-b border-sage-100">
                  <span className="text-[10px] uppercase tracking-wide text-sage-500">
                    {section.appName}
                  </span>
                  {section.unreadCount > 0 && (
                    <button
                      type="button"
                      onClick={() => onMarkAllRead(section.appId)}
                      className="inline-flex items-center gap-1 text-[11px] text-sage-600 hover:text-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 rounded px-1"
                    >
                      <Check className="h-3 w-3" />
                      Mark all read
                    </button>
                  )}
                </div>
                {section.items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    role="menuitem"
                    onClick={() => onItemClick(section.appId, item.id)}
                    className={cn(
                      'w-full text-left px-4 py-2 text-sm text-sage-700 hover:bg-sage-50 focus-visible:bg-sage-50 focus-visible:outline-none flex items-start gap-2',
                      item.read && 'opacity-60',
                    )}
                  >
                    {!item.read && (
                      <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-sage-600" aria-hidden />
                    )}
                    <span className="flex-1">
                      <span className="block">{item.label}</span>
                      {item.sublabel && (
                        <span className="block text-xs text-sage-500">{item.sublabel}</span>
                      )}
                    </span>
                    {item.badge && (
                      <span className="shrink-0 text-[10px] font-semibold rounded-full px-1.5 py-0.5 bg-sage-100 text-sage-800">
                        {item.badge}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
